import { Avatar, Box, Stack, Tooltip } from '@mui/material';

import { gql } from '@graphql/__generated__';
import type { SalesLeadDetailFragment } from '@graphql/__generated__/graphql';
import { leadDetailContainerSx } from '@modules/_shared/components/lead/leadDetailContainerSx';
import { LeadPropertyDetail } from '@modules/_shared/components/lead/LeadPropertyDetail';
import { LeadQualificationFunnel } from '@modules/_shared/components/lead/LeadQualificationFunnel';
import { LeadUpdatedTimeAgo } from '@modules/_shared/components/lead/LeadUpdatedTimeAgo';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getAppraisalPerception } from '@modules/_shared/utils/appraisal';
import {
  formatAddress,
  formatCurrencyRange,
} from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import type { Currency } from '@shared/LocaleContext';
import { getCountryName } from '@shared/utils/countries';
import { getHostName } from '@shared/utils/host-name';

export const SALES_LEAD_DETAIL_FRAGMENT = gql(/* GraphQL */ `
  fragment SalesLeadDetail on leads {
    id
    ...LeadQualificationFunnel
    ...LeadUpdatedTimeAgo
    referrer
    referring_domain
    landing_page_url
    appraisal_perceived_value
    appraisal_perception
    appraisal_property_usage
    appraisal_property_occupied
    appraisal_property_route
    appraisal_property_street_number
    appraisal_property_postcode
    appraisal_property_locality
    appraisal_property_state
    appraisal_property_country_code
    property {
      ...LeadPropertyDetail
      street_number
      route
      postcode
      locality
      state
      country_code
      latest_appraisal {
        id
        max
        min
      }
    }
    anonymized_contact
    contact {
      id
      first_name
      last_name
      referrer
    }
  }
`);

type SalesLeadDetailProps = {
  isCompact: boolean;
  lead: SalesLeadDetailFragment;
  canViewLeadDetails: boolean;
  customCreationDate: string | null;
  currency: Currency;
  showAddress?: boolean;
  bottomButton?: React.ReactNode;
  controls?: React.ReactNode;
};

const getLeadAddress = (
  lead: SalesLeadDetailFragment,
  canViewLeadDetails: boolean,
) => {
  const propertyFormattedAddress = formatAddress(
    {
      ...lead.property,
      country: getCountryName(lead.property?.country_code ?? null),
    },
    ', ',
    false,
  );
  const appraisalPropertyFormattedAddress = formatAddress(
    {
      route: lead.appraisal_property_route,
      street_number: lead.appraisal_property_street_number,
      postcode: lead.appraisal_property_postcode,
      locality: lead.appraisal_property_locality,
      state: lead.appraisal_property_state,
      country: getCountryName(lead.appraisal_property_country_code ?? null),
    },
    ', ',
    false,
  );

  return canViewLeadDetails
    ? propertyFormattedAddress.length > 0
      ? propertyFormattedAddress
      : appraisalPropertyFormattedAddress
    : formatAddress(
        {
          postcode: lead.property?.postcode,
          locality: lead.property?.locality,
          state: lead.property?.state,
          country: getCountryName(lead.property?.country_code ?? null),
        },
        ', ',
        false,
      );
};

const LeadRefererAvatar: React.FC<{ lead: SalesLeadDetailFragment }> = ({
  lead,
}) => (
  <Tooltip
    title={
      <>
        <div>User: {lead.contact?.referrer}</div>
        <div>Lead: {lead.referrer}</div>
        <div>Landing page: {lead.landing_page_url}</div>
      </>
    }
  >
    <Avatar
      sx={{ height: 18, width: 18, marginLeft: 8 }}
      src={`https://www.google.com/s2/favicons?domain=${
        lead.contact?.referrer != null
          ? getHostName(lead.contact.referrer)
          : lead.referring_domain || ''
      }`}
    />
  </Tooltip>
);

export const SalesLeadDetail: React.FC<SalesLeadDetailProps> = ({
  isCompact,
  lead,
  canViewLeadDetails,
  customCreationDate,
  currency,
  showAddress = false,
  bottomButton,
  controls = null,
}) => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const isAdmin = me?.is_admin || false;

  const compactPb = isCompact ? 0 : 1;

  const compactBodyTypography = {
    typography: isCompact ? 'body2' : 'subtitle2',
    fontWeight: isCompact ? 600 : undefined,
    lineHeight: isCompact ? 1.43 : undefined,
  } as const;

  const contact = !canViewLeadDetails
    ? lead.anonymized_contact
    : lead.contact?.first_name != null || lead.contact?.last_name != null
    ? [lead.contact?.first_name, lead.contact?.last_name]
        .filter(Boolean)
        .join(' ')
    : null;

  return (
    <Stack spacing={0.5} sx={leadDetailContainerSx(isCompact)}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          flexWrap: 'nowrap',
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={theme => ({
              m: 0,
              pb: compactPb,
              ...compactBodyTypography,
              ...(isCompact ? theme.mixins.truncate() : {}),
              minWidth: 0,
            })}
          >
            {contact ?? t('Unknown')}
          </Box>
          {!isCompact && (lead.referrer != null || lead.contact?.referrer) && (
            <LeadRefererAvatar lead={lead} />
          )}
        </Stack>
        {!isCompact && (
          <>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                typography: 'caption',
                color: 'text.secondary',
              }}
            >
              <LeadUpdatedTimeAgo
                lead={lead}
                customCreationDate={customCreationDate}
                showBothDate={isAdmin ?? false}
              />
            </Stack>
            {controls != null && (
              <Box
                sx={{
                  pl: 2,
                }}
              >
                {controls}
              </Box>
            )}
          </>
        )}
      </Stack>
      <Box
        sx={theme => ({
          m: 0,
          pb: compactPb,
          ...theme.mixins.truncate(),
          typography: 'caption',
          fontSize: isCompact ? '0.75rem' : undefined,
          lineHeight: isCompact ? 1.33 : undefined,
          color: 'text.secondary',
        })}
      >
        <LeadQualificationFunnel lead={lead} />
      </Box>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: 'center',
          minWidth: 0,
          flexWrap: isCompact ? 'nowrap' : 'wrap',
          pb: compactPb,
          ...compactBodyTypography,
        }}
      >
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {getAppraisalPerception(
            lead.appraisal_perception,
            lead.appraisal_perceived_value,
            lead.property?.latest_appraisal?.max,
            lead.property?.latest_appraisal?.min,
            16,
          )}
        </Box>
        {lead.property?.latest_appraisal != null && (
          <Box
            sx={theme => ({
              minWidth: 0,
              ...(isCompact ? theme.mixins.truncate() : {}),
            })}
          >
            {formatCurrencyRange(
              lead.property?.latest_appraisal,
              currency,
              locale,
            )}
          </Box>
        )}
      </Stack>
      {!isCompact && (
        <LeadPropertyDetail
          property={lead.property}
          currency={currency}
          appraisalPropertyOccupied={lead.appraisal_property_occupied}
          appraisalPropertyUsage={lead.appraisal_property_usage}
        />
      )}
      {!isCompact && showAddress && (
        <Box sx={{ typography: 'body2' }}>
          {getLeadAddress(lead, canViewLeadDetails)}
        </Box>
      )}
      {bottomButton != null && (
        <Box
          sx={{
            mt: 2,
          }}
        >
          {bottomButton}
        </Box>
      )}
    </Stack>
  );
};
